// ---------------------------------------------------------------
// Active-alert detection for each vehicle, with plain-language info
// ---------------------------------------------------------------
import { tempLimits } from './vehicleHealth';

// Common OBD-II fault codes, so customers see words, not just codes
const DTC_MEANINGS = {
  P0300: 'Random/multiple cylinder misfire detected',
  P0301: 'Cylinder 1 misfire', P0302: 'Cylinder 2 misfire', P0303: 'Cylinder 3 misfire',
  P0304: 'Cylinder 4 misfire', P0305: 'Cylinder 5 misfire', P0306: 'Cylinder 6 misfire',
  P0171: 'Engine running too lean (bank 1)', P0174: 'Engine running too lean (bank 2)',
  P0172: 'Engine running too rich (bank 1)', P0175: 'Engine running too rich (bank 2)',
  P0420: 'Catalytic converter efficiency low (bank 1)', P0430: 'Catalytic converter efficiency low (bank 2)',
  P0442: 'EVAP system small leak (often a loose gas cap)', P0455: 'EVAP system large leak',
  P0128: 'Engine coolant staying too cold (thermostat)', P0401: 'EGR flow insufficient',
  P0011: 'Camshaft timing over-advanced (bank 1)', P0016: 'Crankshaft/camshaft position correlation',
  P0562: 'System voltage low', P0700: 'Transmission control system fault',
};

export function explainDtc(code) {
  const c = code.trim().toUpperCase();
  if (DTC_MEANINGS[c]) return DTC_MEANINGS[c];
  const area = { P: 'Powertrain (engine/transmission)', C: 'Chassis (brakes/suspension)', B: 'Body (interior systems)', U: 'Network (module communication)' }[c[0]];
  return area ? `${area} fault — look up ${c} for the exact meaning` : 'Unknown code';
}

// Pull the actual DTC code strings out of a position's attributes. Trackers put
// them under different names/formats depending on firmware/decoder (Teltonika
// AVL 281 = io281, but decoders also use dtcs / dtc / faultCodes, and the value
// may be a single string or a comma/space list). We scan ALL attributes for
// anything shaped like a DTC (P/C/B/U + 4 digits) so it works regardless of key.
// Returns [{ code, meaning }]. Empty if the device only sent a count.
export function dtcCodesFromAttrs(a = {}) {
  if (!a || typeof a !== 'object') return [];
  const found = new Set();
  const scan = (raw) => {
    if (raw == null || raw === '' || raw === 0) return;
    String(raw).split(/[,;\s|/]+/).forEach((s) => {
      const t = s.trim().toUpperCase();
      if (/^[PCBU]\d{4}$/.test(t)) found.add(t);
    });
  };
  // known/likely keys first
  [a.io281, a.dtcs, a.dtc, a.faultCodes, a.troubleCodes, a.dtcFaults, a.dtcNumber].forEach(scan);
  // plus any attribute whose key mentions dtc / fault / trouble
  Object.keys(a).forEach((k) => { if (/dtc|fault|trouble/i.test(k)) scan(a[k]); });
  // last resort: Teltonika sends the code under a numeric io field, so scan EVERY
  // string-valued attribute for a DTC-shaped token. Skip vin/serial-type fields so
  // an alphanumeric VIN can't accidentally match.
  Object.keys(a).forEach((k) => {
    if (/vin|serial|imei|iccid/i.test(k)) return;
    const v = a[k];
    if (typeof v === 'string' && /[PCBU]\d{4}/.test(v)) scan(v);
  });
  return [...found].map((c) => ({ code: c, meaning: explainDtc(c) }));
}

// ---- VIN watch: remembers each tracker's vehicle and flags a swap ----
const VIN_KEY = 'dd-expected-vins';

export function getExpectedVins() {
  try { return JSON.parse(localStorage.getItem(VIN_KEY)) || {}; } catch { return {}; }
}

export function acceptVin(deviceId, vin) {
  const reg = getExpectedVins();
  reg[deviceId] = { vin, at: new Date().toISOString() };
  localStorage.setItem(VIN_KEY, JSON.stringify(reg));
}

// ---- driver credential alerts (CDL, medical card) ----
export function getDriverAlerts(driver) {
  const alerts = [];
  const A = (driver && driver.attributes) || {};
  const soon = (iso) => {
    if (!iso) return null;
    const days = Math.ceil((new Date(iso) - Date.now()) / 864e5);
    return days;
  };

  const cdlDays = soon((A.cdl || {}).expiry);
  if (cdlDays != null && cdlDays < 0) {
    alerts.push({ severity: 'bad', icon: '🪪', title: `CDL EXPIRED ${Math.abs(cdlDays)} day(s) ago`, info: `${driver.name}'s CDL expired on ${new Date(A.cdl.expiry).toLocaleDateString()}. The driver should not be operating until renewed.` });
  } else if (cdlDays != null && cdlDays <= 30) {
    alerts.push({ severity: 'warn', icon: '🪪', title: `CDL expires in ${cdlDays} day(s)`, info: `${driver.name}'s CDL expires on ${new Date(A.cdl.expiry).toLocaleDateString()}. Schedule the renewal now.` });
  }

  // annual DQ renewals that have lapsed (MVR review, cert of violations, Clearinghouse annual)
  const safety = A.safety || {};
  const annualChecks = [
    ['mvrAnnual', 'Annual MVR review'],
    ['violationCert', 'Annual certificate of violations'],
    ['clearinghouseAnnual', 'Clearinghouse annual query'],
  ];
  annualChecks.forEach(([key, name]) => {
    const rec = safety[key];
    if (rec && rec.date) {
      const ageDays = (Date.now() - new Date(rec.date)) / 864e5;
      if (ageDays > 365) alerts.push({ severity: 'bad', icon: '📋', title: `${name} overdue`, info: `${driver.name}'s ${name.toLowerCase()} was last done ${new Date(rec.date).toLocaleDateString()} — it renews annually and is now overdue.` });
      else if (ageDays > 335) alerts.push({ severity: 'warn', icon: '📋', title: `${name} due soon`, info: `${driver.name}'s ${name.toLowerCase()} renews within 30 days.` });
    }
  });

  const medDays = soon(A.medicalExpiry);
  if (medDays != null && medDays < 0) {
    alerts.push({ severity: 'bad', icon: '🏥', title: `Medical card EXPIRED ${Math.abs(medDays)} day(s) ago`, info: `${driver.name}'s DOT medical card expired on ${new Date(A.medicalExpiry).toLocaleDateString()}.` });
  } else if (medDays != null && medDays <= 30) {
    alerts.push({ severity: 'warn', icon: '🏥', title: `Medical card expires in ${medDays} day(s)`, info: `${driver.name}'s DOT medical card expires on ${new Date(A.medicalExpiry).toLocaleDateString()}. Book the exam now.` });
  }

  return alerts;
}

// Returns an array of active alerts for one vehicle's latest position + device
export function getAlerts(device, position) {
  const alerts = [];
  const a = (position && position.attributes) || {};

  // VIN change detection (tracker moved to a different car, or tampering)
  if (device && a.vin) {
    const expected = getExpectedVins()[device.id];
    if (!expected) {
      acceptVin(device.id, a.vin); // first VIN ever seen — learn it silently
    } else if (expected.vin !== a.vin) {
      alerts.push({
        severity: 'bad', icon: '🆔', title: 'VIN changed — tracker may have been moved or tampered with',
        info: `This tracker has reported VIN ${expected.vin} since ${new Date(expected.at).toLocaleDateString()}, but now reports VIN ${a.vin}. If the device was intentionally installed in a different vehicle, accept the new VIN below.`,
        type: 'vin', newVin: a.vin,
      });
    }
  }

  // signal jamming — GPS/GSM being blocked (classic vehicle-theft tactic)
  if (a.jamming === true || Number(a.io249) === 1 || a.blocked === true) {
    alerts.push({
      severity: 'bad', icon: '📡', title: 'Signal jamming detected',
      ackKey: 'jamming', ackSig: '1',
      info: 'The tracker is detecting GPS/GSM jamming — someone may be blocking the signal to hide the vehicle. Verify the vehicle immediately.',
    });
  }

  // NOTE: we deliberately do NOT alert just because Traccar marks the device
  // "offline". An OBD tracker SLEEPS whenever the car is parked, so it goes
  // offline on every normal park — firing a "Tracker offline" warning here was
  // misleading customers into thinking the device had been unplugged. Genuine
  // disconnects (unplugged mid-drive, or an always-powered tracker that dies
  // while parked) are detected properly by checkDeviceLinks() in deviceAlerts.js,
  // which waits out a grace period and learns whether the port is always powered.

  if ((a.io30 || 0) > 0) {
    // DTC strings can arrive under a few names depending on the tracker/decoder
    const rawCodes = a.io281 != null ? a.io281 : (a.dtcs != null ? a.dtcs : (a.dtc != null ? a.dtc : a.faultCodes));
    const codes = rawCodes != null
      ? String(rawCodes).split(/[,;\s]+/).filter(Boolean)
      : [];
    alerts.push({
      severity: 'bad', icon: '🚨', title: `Check Engine — ${a.io30} active fault code(s)`,
      ackKey: 'check-engine', ackSig: String(a.io30),
      info: codes.length
        ? null
        : 'The engine computer reports active faults. Enable fault-code reporting (AVL 281) to see the exact codes here.',
      codes: codes.map((c) => ({ code: c.toUpperCase(), meaning: explainDtc(c) })),
    });
  }

  if (a.io32 != null) {
    const lim = tempLimits(a);
    if (a.io32 > lim.hot) {
      alerts.push({ severity: 'bad', icon: '🔥', title: `Engine overheating — ${a.io32} °C`, ackKey: 'engine-temp', ackSig: String(a.io32), info: `Over the ${lim.hot} °C limit. The vehicle should stop as soon as it is safe.` });
    } else if (a.io32 > lim.warn) {
      alerts.push({ severity: 'warn', icon: '🌡️', title: `Engine running hot — ${a.io32} °C`, ackKey: 'engine-temp', ackSig: String(a.io32), info: `Above this car's ${lim.warn} °C threshold. Most engines run 90–110 °C. Monitor closely.` });
    }
  }

  if (a.power != null) {
    if (a.ignition && a.power < 12.4) {
      alerts.push({ severity: 'bad', icon: '🪫', title: `Charging problem — ${a.power.toFixed(1)} V while running`, ackKey: 'charging', ackSig: a.power.toFixed(1), info: 'Expected 13.2–14.8 V with the engine on. Alternator or belt may have failed; the vehicle is running off its battery.' });
    } else if (!a.ignition && a.power < 11.8) {
      alerts.push({ severity: 'bad', icon: '🪫', title: `Battery very low — ${a.power.toFixed(1)} V`, ackKey: 'charging', ackSig: a.power.toFixed(1), info: 'The vehicle may not start. Charge or replace the battery.' });
    } else if (!a.ignition && a.power < 12.4) {
      alerts.push({ severity: 'warn', icon: '🔋', title: `Battery getting weak — ${a.power.toFixed(1)} V`, ackKey: 'charging', ackSig: a.power.toFixed(1), info: 'Below 12.4 V at rest. The battery is aging or being drained.' });
    }
  }

  if (a.io48 != null && a.io48 < 10) {
    alerts.push({ severity: 'bad', icon: '⛽', title: `Fuel critical — ${a.io48}%`, ackKey: 'fuel', ackSig: String(a.io48), info: 'Nearly empty.' });
  } else if (a.io48 != null && a.io48 < 25) {
    alerts.push({ severity: 'warn', icon: '⛽', title: `Fuel low — ${a.io48}%`, ackKey: 'fuel', ackSig: String(a.io48), info: 'Plan a fuel stop soon.' });
  }

  if (a.io43 != null && a.io43 > 0 && (a.io30 || 0) === 0) {
    alerts.push({ severity: 'warn', icon: '📋', title: `Past issue: ${a.io43} km driven with warning light on`, ackKey: 'mil-history', ackSig: String(a.io43), info: 'No active fault now, but the vehicle was previously driven with the check-engine light on.' });
  }

  return alerts;
}
